import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { inputErrorState, inputFormValueState } from '../../stores/formState';
import { TranslateRule, translateValue } from './TranslateRules';
import { completedComponentState } from '../../stores/completedComponentState';

export interface TextAreaProps {
  componentId: string;
  name: string;
  rows: number;
  cols?: number;
  optional: boolean;
  message?: string;
  translateRule?: TranslateRule,
  placeholder?: string;
  prefix?: string;
  suffix?: string;
  focus?: boolean;
  autoComplete?: string;
}

const TextArea: React.FC<TextAreaProps> = props => {
  const [value, setValue] = useRecoilState(inputFormValueState(props.componentId));
  const [inputValue, setInputValue] = useState<string | null>(null);
  const [error, setError] = useRecoilState(inputErrorState(props.componentId));
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [composingValue, setComposingValue] = useState<string | null>(null);

  const setTranslatedValue = useCallback((inputValue: string) => {
    if (props.optional && inputValue === '') {
      setInputValue(null);
      setValue(null);
      setError(null);
      return;
    }

    const [value, error] = translateValue(props.translateRule, inputValue);
    setInputValue(value != null ? value : inputValue);
    setValue(value);
    setError(error);
  }, [props.optional, props.translateRule, setValue, setError])

  const onInput = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    if (composingValue != null) {
      setComposingValue(e.target.value);
    } else {
      setTranslatedValue(e.target.value);
    }
  }, [composingValue, setTranslatedValue]);

  useEffect(() => {
    if (props.focus && inputRef.current != null) {
      inputRef.current.focus();
    }
  }, [props.focus, inputRef])

  const setCompleted = useSetRecoilState(completedComponentState(props.componentId));
  const onBlur = useCallback(() => {
    if (value == null || value === '') {
      setCompleted(false)
    } else {
      setCompleted(true)
    }
  }, [value, setCompleted]);

  const onCompositionStart = useCallback((e: React.CompositionEvent<HTMLTextAreaElement>) => {
    if (!(e.target instanceof HTMLTextAreaElement)) return;
    setComposingValue(e.target.value)
  }, []);
  const onCompositionEnd = useCallback(() => {
    if (composingValue == null) return;
    setTranslatedValue(composingValue)
    setComposingValue(null);
  }, [composingValue, setTranslatedValue]);


  const valueStr = composingValue != null ? composingValue : inputValue != null ? String(inputValue) : '';

  return (
    <label className={`text-input ${props.translateRule || 'text'} ${error != null ? 'invalid' : ''}`}>
      <span>
      {props.message ? <span className="message">{props.message}</span> : ''}
      <span className="optional">({props.optional ? "任意" : "必須"})</span>
      </span>
      <span className="text-input-inner" data-prefix={props.prefix} data-suffix={props.suffix}>
        <textarea
          name={props.name}
          placeholder={props.placeholder}
          autoComplete={props.autoComplete}
          cols={props.cols}
          rows={props.rows}
          onChange={onInput}
          onCompositionStart={onCompositionStart}
          onCompositionEnd={onCompositionEnd}
          onBlur={onBlur}
          ref={inputRef}
          value={valueStr}
        />
      </span>
      <span className="error-message">{error}</span>
    </label>
  );
};

export default TextArea;
